import {
  getResolutionStatus,
  getSessionDelta,
  getSessionEffort,
  getSessionLoop,
  getSessionPriority,
  isTooShortForJudge,
  isV2Session
} from './sessionFields';

export const countSentimentDistribution = (sessions = []) => {
  const sentimentCounts = sessions.reduce((acc, session) => {
    const sentiment = session.overall_sentiment;
    if (!sentiment) {
      acc.noSentiment++;
      return acc;
    }

    if (sentiment === 'very_positive') acc.very_positive++;
    else if (sentiment === 'positive') acc.positive++;
    else if (sentiment === 'neutral') acc.neutral++;
    else if (sentiment === 'negative') acc.negative++;
    else if (sentiment === 'very_negative') acc.very_negative++;
    else acc.noSentiment++;

    return acc;
  }, { very_positive: 0, positive: 0, neutral: 0, negative: 0, very_negative: 0, noSentiment: 0 });

  return {
    counts: sentimentCounts,
    stats: {
      positive: sentimentCounts.positive + sentimentCounts.very_positive,
      negative: sentimentCounts.negative + sentimentCounts.very_negative,
      neutral: sentimentCounts.neutral,
      very_positive: sentimentCounts.very_positive,
      very_negative: sentimentCounts.very_negative
    }
  };
};

export const buildResolutionStats = (sessions = []) => {
  const resolutionCounts = sessions.reduce((acc, session) => {
    const resolution = getResolutionStatus(session);

    if (resolution === 'resolved' || resolution === 'partially_resolved' || resolution === 'closed' || resolution === 'satisfied') {
      acc.resolved++;
    } else if (resolution === 'unresolved' || resolution === 'open' || resolution === 'pending') {
      acc.unresolved++;
    } else {
      acc.unknown++;
    }

    acc.total++;
    return acc;
  }, { resolved: 0, unresolved: 0, unknown: 0, total: 0 });

  const sessionsWithStatus = resolutionCounts.resolved + resolutionCounts.unresolved;

  return {
    resolved: resolutionCounts.resolved,
    unresolved: resolutionCounts.unresolved,
    unknown: resolutionCounts.unknown,
    total: resolutionCounts.total,
    sessionsWithStatus,
    resolutionPercentage: sessionsWithStatus > 0
      ? ((resolutionCounts.resolved / sessionsWithStatus) * 100).toFixed(1)
      : 0
  };
};

export const buildEffortStats = (sessions = []) => {
  const effortDist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  let effortSum = 0;
  let effortCount = 0;

  sessions.forEach((session) => {
    const score = getSessionEffort(session);
    if (score && score >= 1 && score <= 5) {
      effortDist[score]++;
      effortSum += score;
      effortCount++;
    }
  });

  const highEffortCount = effortDist[4] + effortDist[5];

  return {
    distribution: effortDist,
    average: effortCount > 0 ? (effortSum / effortCount).toFixed(1) : 0,
    total: effortCount,
    highEffortCount,
    highEffortPercentage: effortCount > 0
      ? ((highEffortCount / effortCount) * 100).toFixed(1)
      : 0
  };
};

export const buildPriorityStats = (sessions = []) => {
  const priorityDist = { P0: 0, P1: 0, P2: 0, P3: 0 };
  let priorityCount = 0;

  sessions.forEach((session) => {
    const risk = getSessionPriority(session);
    if (risk && Object.prototype.hasOwnProperty.call(priorityDist, risk)) {
      priorityDist[risk]++;
      priorityCount++;
    }
  });

  const actionRequired = priorityDist.P0 + priorityDist.P1;

  return {
    distribution: priorityDist,
    total: priorityCount,
    actionRequired,
    actionRequiredPercentage: priorityCount > 0
      ? ((actionRequired / priorityCount) * 100).toFixed(1)
      : 0
  };
};

export const buildTrajectoryStats = (sessions = []) => {
  let improving = 0;
  let declining = 0;
  let stable = 0;
  let noTrajectoryData = 0;
  let deltaSum = 0;
  let deltaCount = 0;

  sessions.forEach((session) => {
    const delta = getSessionDelta(session);
    if (delta === null || delta === undefined) {
      noTrajectoryData++;
      return;
    }

    const deltaNum = parseFloat(delta);
    if (Number.isNaN(deltaNum)) {
      noTrajectoryData++;
    } else if (deltaNum > 0) {
      improving++;
      deltaSum += deltaNum;
      deltaCount++;
    } else if (deltaNum < 0) {
      declining++;
      deltaSum += deltaNum;
      deltaCount++;
    } else {
      stable++;
      deltaCount++;
    }
  });

  let hiddenDipCount = 0;
  sessions.forEach((session) => {
    if (session.core_min_valence != null) {
      const coreMin = parseFloat(session.core_min_valence);
      const closing = parseFloat(session.closing_valence);
      if (!Number.isNaN(coreMin) && coreMin <= -0.5 && !Number.isNaN(closing) && closing >= 0) {
        hiddenDipCount++;
      }
    }
  });

  return {
    improving,
    declining,
    stable,
    noData: noTrajectoryData,
    averageDelta: deltaCount > 0 ? (deltaSum / deltaCount).toFixed(2) : 0,
    total: deltaCount,
    hiddenDips: hiddenDipCount
  };
};

export const buildRiskStats = (sessions = []) => {
  const v2Count = sessions.length;
  let exitIntentCount = 0;
  let closingMissingCount = 0;

  sessions.forEach((session) => {
    if (session.exit_intent === true) exitIntentCount++;
    if (session.closing_missing === true) closingMissingCount++;
  });

  return {
    exitIntentCount,
    exitIntentPercentage: v2Count > 0
      ? ((exitIntentCount / v2Count) * 100).toFixed(1)
      : 0,
    closingMissingCount,
    closingMissingPercentage: v2Count > 0
      ? ((closingMissingCount / v2Count) * 100).toFixed(1)
      : 0,
    total: v2Count
  };
};

export const buildLoopStats = (sessions = []) => {
  const v2Count = sessions.length;
  let loopSessionCount = 0;

  sessions.forEach((session) => {
    if (getSessionLoop(session)) loopSessionCount++;
  });

  return {
    loopCount: loopSessionCount,
    loopPercentage: v2Count > 0
      ? ((loopSessionCount / v2Count) * 100).toFixed(1)
      : 0,
    total: v2Count
  };
};

export const filterV2Sessions = (sessions = []) => sessions.filter(isV2Session);

export const filterJudgeScoredSessions = (sessions = []) =>
  filterV2Sessions(sessions).filter((session) => !isTooShortForJudge(session));

export const isJudgeConfidence = (session) => {
  if (isTooShortForJudge(session)) return false;
  const conf = parseFloat(session?.confidence);
  return Number.isFinite(conf) && conf > 0 && conf <= 1;
};

export const filterPipelineSessions = (sessions = [], version = null) => {
  if (!version) return sessions;
  return sessions.filter((session) => session?.pipeline_version === version);
};

export { getResolutionStatus, getSessionPriority };
